import React from "react";
import { useControlled, noop } from "../../hooks";
import { Collapsible, CollapsibleProps } from "../Collapsible";
import {
  Box,
  BoxProps,
  IconDownArrow,
  Text,
  Flex,
  themeGet,
  styled,
} from "../../";

type Context = {
  expanded: boolean;
  setExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  focused: boolean;
  setFocused: React.Dispatch<React.SetStateAction<boolean>>;
};

const Context = React.createContext<Context | null>(null);

export type AccordionProps = {
  /** Whether to show top divider*/
  showDividerTop?: boolean;
  /** Whether to show bottom divider*/
  showDividerBottom?: boolean;
  /** Whether to show the expandable content */
  expanded?: boolean;
  /** Callback that fires when the expanded value changes */
  onChange?: (expanded: boolean) => void;
} & Omit<BoxProps, "onChange">;

export const Accordion = ({
  expanded: _expanded,
  onChange = noop,
  showDividerBottom = true,
  showDividerTop = true,
  ...boxProps
}: AccordionProps) => {
  const [expanded, setExpanded] = useControlled(false, _expanded, onChange);
  const [focused, setFocused] = React.useState(false);

  return (
    <Context.Provider value={{ expanded, setExpanded, focused, setFocused }}>
      <Box
        borderTop={showDividerTop ? "1px solid #ddd" : undefined}
        borderBottom={showDividerBottom ? "1px solid #ddd" : undefined}
        {...boxProps}
      />
    </Context.Provider>
  );
};

export type AccordionSummaryProps = {
  /** Alignment of summary text applicable when using string children */
  align?: BoxProps["textAlign"];
  /** Allow icon rotation animation */
  allowIconRotation?: boolean;
  /** Duration of icon rotation in ms */
  iconRotationDuration?: number;
  children:
    | React.ReactNode
    | ((props: { focused: boolean }) => React.ReactNode);
} & Omit<BoxProps, "children" | "ref">;

export const AccordionSummary = ({
  allowIconRotation = true,
  iconRotationDuration = 200,
  children,
  align,
  ...boxProps
}: AccordionSummaryProps) => {
  const context = React.useContext(Context);

  if (!context) {
    throw new Error(
      "<AccordionSummary /> must be rendered inside an <Accordion />"
    );
  }
  const { expanded, setExpanded, focused, setFocused } = context;

  const rotationStyles = allowIconRotation
    ? {
        transform: `rotate(${expanded ? 180 : 0}deg)`,
        transition: `transform ${iconRotationDuration}ms ease-in-out`,
      }
    : undefined;

  return (
    <AccordionSummaryContainer
      tabIndex={0}
      onKeyPress={(e) => {
        boxProps.onKeyPress?.(e);
        if (e.key === "Enter") {
          setExpanded((val) => !val);
        }
      }}
      onFocus={(e) => {
        boxProps.onFocus?.(e);
        setFocused(true);
      }}
      onBlur={(e) => {
        boxProps.onBlur?.(e);
        setFocused(false);
      }}
      role="button"
      aria-expanded={expanded}
      onClick={() => setExpanded((val) => !val)}
      position="relative"
      alignItems="center"
      cursor="pointer"
      padding="1.375rem 2rem 1.3755rem 1rem"
      {...boxProps}
    >
      <Box flexGrow={1}>
        {typeof children === "string" || typeof children === "number" ? (
          <Text
            margin={0}
            fontSize={boxProps.fontSize}
            fontWeight={boxProps.fontWeight ?? 600}
            textAlign={align}
            color="inherit"
          >
            {children}
          </Text>
        ) : typeof children === "function" ? (
          children({ focused })
        ) : (
          children
        )}
      </Box>
      <Flex
        alignItems="center"
        justifyContent="center"
        position="absolute"
        top="0"
        right="0"
        height="100%"
        width="48px"
      >
        <Box style={rotationStyles}>
          <IconDownArrow height={12} width={12} />
        </Box>
      </Flex>
    </AccordionSummaryContainer>
  );
};

const AccordionSummaryContainer = styled(Flex)`
  &:hover {
    svg {
      color: ${themeGet("colors.secondaryDark")};
    }
    color: ${themeGet("colors.secondaryDark")};
  }

  &:focus {
    outline: none;
  }

  &:focus-visible {
    color: ${themeGet("colors.secondaryDark")};
    svg {
      color: ${themeGet("colors.secondaryDark")};
    }
  }
`;

export type AccordionDetailsProps = {
  CollapsibleProps?: CollapsibleProps;
} & BoxProps;

export const AccordionDetails: React.FC<AccordionDetailsProps> = ({
  children,
  CollapsibleProps,
  ...boxProps
}) => {
  const context = React.useContext(Context);
  if (!context) {
    throw new Error(
      "<AccordionSummary /> must be rendered inside an <Accordion />"
    );
  }
  const { expanded } = context;

  return (
    <Collapsible show={expanded} {...CollapsibleProps}>
      <Box padding="0rem 1rem 1.375rem 1rem" {...boxProps}>
        {typeof children === "string" ? (
          <Text
            margin={0}
            fontSize={boxProps.fontSize}
            fontWeight={boxProps.fontWeight ?? 600}
          >
            {children}
          </Text>
        ) : (
          children
        )}
      </Box>
    </Collapsible>
  );
};

Accordion.displayName = "Accordion";

export default Accordion;
